import { BlockPadding, Curtain } from "@business-finland/wif-ui-lib";
import { SectionHeader } from "@business-finland/wif-ui-lib/dist/SectionHeader";
import { JobList } from "@business-finland/wif-ui-lib/dist/JobList";
import { Pagination } from "@business-finland/wif-ui-lib/dist/Pagination";
import { RefObject, useEffect, useReducer, useRef } from "react";
import { useTranslation } from "next-i18next";

import {
  OpenJobsStateContext,
  OpenJobsDispatchContext,
  useOpenJobsState,
  useOpenJobsDispatch,
} from "./context";
import { ACTIONS, openJobsInitialState, openJobsStateReducer } from "./store";
import OpenJobsHeader from "./OpenJobsHeader";
import useOpenJobsSearch from "./useOpenJobsSearch";
import {
  headerHeightSmAndAbove,
  headerHeightXs,
  Viewport,
} from "../../../../constants/app";
import useViewport from "../../../../contexts/ViewportContext";
import { getJobsPageSize } from "../../../../util/jobs-list";
import {
  resolveJobsPageBackgroundColor,
  uiBackgroundColorHex,
} from "../../../../util/cms-background-color";

export interface IOpenJobsProps extends Wif.Mgnl.OpenJobsPage {}

export default function OpenJobs(props: IOpenJobsProps): JSX.Element {
  const [state, dispatch] = useReducer(
    openJobsStateReducer,
    openJobsInitialState
  );

  useEffect(() => {
    dispatch(ACTIONS.initState(props.categories));
  }, [dispatch, props.categories]);

  const { isMobile } = useViewport();
  const pageSize = getJobsPageSize(isMobile);

  useOpenJobsSearch(state, dispatch, props.campaignId, pageSize);

  const pageBackgroundColor = resolveJobsPageBackgroundColor(
    props.backgroundColor
  );

  const jobCardsSectionRef = useRef<HTMLDivElement>(null);

  const { isCurtainVisible, totalJobs, jobCards, page } = state;

  return (
    <OpenJobsDispatchContext.Provider value={dispatch}>
      <OpenJobsStateContext.Provider value={state}>
        <div
          style={{
            backgroundColor: uiBackgroundColorHex(
              pageBackgroundColor,
              "#f5f5f5"
            ),
          }}
        >
          <OpenJobsHeader {...props} />

          <div
            ref={jobCardsSectionRef}
            style={{ position: "relative", zIndex: 0 }}
          >
            <Curtain show={isCurtainVisible} />

            <div style={{ visibility: jobCards ? "visible" : "hidden" }}>
              <SectionHeader>
                {totalJobs > 0
                  ? `There are ${totalJobs} open jobs right now`
                  : "0 jobs available"}
              </SectionHeader>
            </div>

            <JobList
              page={page}
              pageSize={pageSize}
              showRange={false}
              bottomPadding={BlockPadding.SMALL}
              jobCards={jobCards}
              showLogo={props.showJobLogos !== false}
              backgroundColor={pageBackgroundColor}
              skeletonLoadingCount={pageSize}
            />

            <OpenJobsPagination jobCardsSectionRef={jobCardsSectionRef} />
          </div>
        </div>
      </OpenJobsStateContext.Provider>
    </OpenJobsDispatchContext.Provider>
  );
}

function OpenJobsPagination(props: {
  jobCardsSectionRef: RefObject<HTMLDivElement>;
}): JSX.Element | null {
  const { t } = useTranslation("common");
  const { totalPages, page } = useOpenJobsState();
  const dispatch = useOpenJobsDispatch();
  const { viewport } = useViewport();

  const scrollToJobCardsStart = () => {
    if (props.jobCardsSectionRef.current) {
      const offsetJobCardSection = props.jobCardsSectionRef.current.offsetTop;
      const headerHeight =
        viewport === Viewport.xs ? headerHeightXs : headerHeightSmAndAbove;

      const offsetTotal = offsetJobCardSection - headerHeight;

      window.scrollTo(0, offsetTotal);
    }
  };
  const onPagination = (page: number) => {
    dispatch({ page });
    scrollToJobCardsStart();
  };

  if (totalPages === 0) {
    return null;
  }

  return (
    <Pagination
      count={totalPages}
      onChange={onPagination}
      value={page}
      i18n={{
        btnPrevText: t("pagination.previousPage"),
        btnNextText: t("pagination.nextPage"),
        btnPageText: t("pagination.goToPage"),
      }}
    />
  );
}
